import { useApiQuery } from "../../../services/api";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

// What a user can reference into a Juisci GPT conversation, per kind:
//
// - PLAYLISTS: the ones they CREATED (`/playlists/me`) plus the ones they SAVED
//   (`/users/:id/saved-playlists`) — the two sets the POST endpoint authorizes.
//   A closed, small catalogue, so it is fetched whole and filtered client-side.
// - ARTICLES: the whole Juisci corpus they can read, which is far too large to
//   list — so it is a SERVER search, debounced, and there is nothing to show
//   before the user types.
//
// Both reduce to the minimal `{ id, title }` the composer pickers + the `@`/`+`
// autocomplete render.

export type ReferenceableSource = { id: string; title: string };

// Enough for the picker/autocomplete without paging: a user with more than this
// many playlists is far past what a mid-composer list should scroll.
const PLAYLIST_LIMIT = 50;

// Article search results shown at once. A composer dropdown is a shortlist —
// refining the query beats scrolling a hundred papers.
const ARTICLE_SEARCH_LIMIT = 12;

// Below this the query is too broad to mean anything (and every keystroke would
// cost a full corpus search).
export const MIN_ARTICLE_QUERY_LENGTH = 2;

// One search per pause in typing, not per keystroke.
const ARTICLE_SEARCH_DEBOUNCE_MS = 300;

export function useReferenceablePlaylists(enabled: boolean): {
  playlists: ReferenceableSource[];
  isLoading: boolean;
} {
  const { currentUser } = useCurrentUser();
  const userId = currentUser === null ? null : currentUser.id;

  const createdQuery = useApiQuery(
    "/webapp/private/playlists/me",
    enabled ? { query: { limit: PLAYLIST_LIMIT, offset: 0 } } : false
  );
  const savedQuery = useApiQuery(
    "/webapp/private/users/:id/saved-playlists",
    enabled && userId !== null ? { param: { id: userId } } : false
  );

  const created =
    createdQuery.state === "success" ? createdQuery.body.docs : [];
  const saved = savedQuery.state === "success" ? savedQuery.body : [];

  const byId = new Map<string, ReferenceableSource>();
  for (const playlist of [...created, ...saved]) {
    if (byId.has(playlist.id)) continue;
    byId.set(playlist.id, { id: playlist.id, title: playlist.title });
  }

  return {
    playlists: [...byId.values()],
    isLoading:
      createdQuery.state === "pending" || savedQuery.state === "pending",
  };
}

/**
 * Articles matching what the user typed, from the corpus they can read (the
 * endpoint is `optionalAuth` and scopes to public + joined-room content — the
 * same grant the POST endpoint re-checks before persisting a reference).
 *
 * `query` is null wherever the surface has no active search (the panel is
 * closed, or the mention trigger is inactive): no request goes out, and the
 * result is empty rather than stale.
 */
export function useReferenceableArticles(query: string | null): {
  articles: ReferenceableSource[];
  isLoading: boolean;
} {
  const trimmed = query === null ? "" : query.trim();
  const debounced = useDebouncedValue(trimmed, ARTICLE_SEARCH_DEBOUNCE_MS);
  const active =
    query !== null && debounced.length >= MIN_ARTICLE_QUERY_LENGTH
      ? debounced
      : null;

  const searchQuery = useApiQuery(
    "/webapp/public/articles/search",
    active === null
      ? false
      : {
          query: {
            search: active,
            limit: ARTICLE_SEARCH_LIMIT,
            offset: 0,
            scoring: "relevant",
            adminPanel: false,
            impactFactorMoreThan: 0,
            // The MeSH resolver is an LLM call per query — worth it on the
            // search page, not behind a composer dropdown the user retypes
            // while a chat is open.
            meshResolver: false,
          },
        }
  );

  return {
    articles:
      searchQuery.state === "success"
        ? searchQuery.body.docs.map((article) => ({
            id: article.id,
            title: article.title,
          }))
        : [],
    // Typing counts as loading: between the keystroke and the debounced request
    // the list is stale, and showing the previous query's papers as if they
    // matched the new one is worse than a spinner.
    isLoading:
      active !== null &&
      (searchQuery.state === "pending" || debounced !== trimmed),
  };
}
